<script setup>
defineProps({
  url: {
    type: String,
    default: () => '',
  },
  image: {
    type: Object,
    default: () => {},
  },
  title: {
    type: String,
    default: () => '',
  },
  description: {
    type: String,
    default: () => '',
  },
  children: {
    type: Array,
    default: () => [],
  },
});
</script>
<template>
  <div class="pages-cards__item">
    <picture v-if="image?.src" class="pages-cards__item-image">
      <source
        v-if="image?.src_mobile"
        media="(max-width: 767px)"
        :srcset="image?.src_mobile"
      />
      <source
        v-if="image?.src_tablet"
        media="(min-width: 768px) and (max-width: 1023px)"
        :srcset="image?.src_tablet"
      />
      <NuxtImg
        :src="image.src"
        :alt="image.alt"
        :height="image.height"
        :width="image.width"
        loading="lazy"
      />
    </picture>
    <div class="pages-cards__item-bottom">
      <div class="pages-cards__item-text">
        <p
          v-if="title"
          class="mb-0 pages-cards__item-title h4"
          v-html="title"
        ></p>
        <div
          v-if="description && description.length > 0"
          class="pages-cards__item-description body-3-regular"
          v-html="description"
        ></div>
        <ul
          v-if="children && children.length > 0"
          class="m-0 p-0 pages-cards__item-children"
        >
          <template
            v-for="(listItem, listItemIndex) in children"
            :key="listItemIndex"
          >
            <li
              v-if="listItem.title"
              class="m-0 pages-cards__item-children-item"
            >
              <Button v-slot="slotProps" as-child variant="text">
                <CTagDetect
                  :to="listItem.url"
                  class="pages-cards__item-children-item-button button-icon-next"
                  :class="[
                    slotProps.class,
                    `button-text-${view}`,
                    `js-button-page-card-${listItemIndex}`,
                  ]"
                >
                  {{ listItem.title }}
                  <CIcon
                    class="button-icon-next-icon"
                    name="arrow-red"
                    width="16"
                    height="16"
                  />
                </CTagDetect>
              </Button>
            </li>
          </template>
        </ul>
      </div>
      <Button v-if="url" v-slot="slotProps" as-child rounded variant="outlined">
        <CTagDetect
          :to="url"
          :class="slotProps.class"
          class="pages-cards__item-button button-details link-normal js-button-page-card"
        >
          <span class="button-details__text">
            <template v-if="children && children.length > 0">
              {{ $t('services.view_all') }}
            </template>
            <template v-else>
              {{ $t('services.more_details') }}
            </template>
          </span>
        </CTagDetect>
      </Button>
    </div>
  </div>
</template>
<style scoped lang="scss">
.pages-cards__item {
  $parent: &;
  background: url() no-repeat center center/cover;
  border-radius: 4px;
  min-height: 224px;
  position: relative;
  overflow: hidden;
  &:after {
    background: linear-gradient(
      186.23deg,
      rgba(19, 18, 18, 0) 42.12%,
      rgba(19, 18, 18, 0.6) 60.62%
    );
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transition: all 0.3s linear;
    width: 100%;
  }
  @include media-breakpoint-up(sm) {
    border-radius: 8px;
    min-height: 244px;
  }
  @include media-breakpoint-up(md) {
    min-height: 400px;
    &::before {
      backdrop-filter: blur(12px);
      background-color: rgba($black, 0.1);
      opacity: 0;
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      transition: all 0.3s linear;
      width: 100%;
    }
    &:hover {
      &::before {
        opacity: 1;
      }
      #{$parent} {
        &-description {
          max-height: fit-content;
          opacity: 1;
        }
        &-children {
          max-height: fit-content;
          opacity: 1;
        }
      }
    }
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(min-height, 400);
    &:hover {
      #{$parent} {
        &-description {
          // @include responsive-spacing(max-height, 200);
        }
        &-children {
          //@include responsive-spacing(max-height, 200);
        }
      }
    }
  }
  &-image {
    display: block;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    position: absolute;
    width: 100%;
  }
  &-bottom {
    align-items: end;
    bottom: 0;
    display: grid;
    grid-template-columns: auto max-content;
    justify-content: space-between;
    left: 0;
    padding: 16px;
    position: absolute;
    width: 100%;
    z-index: 2;
    @include media-breakpoint-up(sm) {
      padding: 24px;
    }
    @include media-breakpoint-up(md) {
      gap: 24px;
      padding: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
      @include responsive-spacing(padding, 32);
    }
  }
  &-title {
    color: $white;
    margin-bottom: 10px;
    @include media-breakpoint-up(sm) {
      margin-bottom: 8px;
    }
    @include media-breakpoint-up(md) {
      grid-area: title;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-bottom, 8);
    }
  }
  &-description {
    display: none;
    color: $white;
    @include media-breakpoint-up(md) {
      display: block;
      transition: all 0.5s ease;
      max-height: 0;
      opacity: 0;
    }
  }
  &-children {
    list-style: none;
    display: none;
    @include media-breakpoint-up(md) {
      display: flex;
      flex-direction: column;
      transition: all 0.5s ease;
      max-height: 0;
      opacity: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 6);
    }
    &-item {
      margin-top: -8px;
      &:first-child {
        margin-top: 0;
      }
      &-button {
        border-radius: 0 !important;
        color: $white;
        width: fit-content;
      }
    }
  }
}
</style>
